import React, { useState } from 'react';
import { Search, Eye, Copy, Send, Zap, BellOff, ChevronLeft, ChevronRight } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { NotificationRecord } from '@/services/PagesServices/MarketingAndNotificationService';
import './NotificationHistory.css';

interface NotificationHistoryProps {
  onViewNotification: (notification: NotificationRecord) => void;
  onNewNotification: () => void;
}

const NotificationHistory: React.FC<NotificationHistoryProps> = ({ onViewNotification, onNewNotification }) => {
  const { notificationHistory } = useMarketingAndNotificationStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const getTypeLabel = (type: string) => {
    if (type === 'email') return 'Email';
    if (type === 'in-app') return 'In-App';
    return 'Both';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return '#10b981'; // Green
      case 'scheduled':
        return '#3b82f6'; // Blue
      case 'draft':
        return '#6b7280'; // Gray
      case 'failed':
        return '#ef4444'; // Red
      case 'sending':
        return '#f59e0b'; // Orange/Amber
      default:
        return '#6b7280'; // Default Gray
    }
  };

  const filteredNotifications = notificationHistory?.filter(notif => {
    const matchesSearch = notif.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || notif.type === filterType;
    const matchesStatus = filterStatus === 'all' || notif.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  }) || [];

  // Pagination calculations
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="admin-marketing-history-content">
      <div className="admin-marketing-history-header">
        <div>
          <h2 className="admin-marketing-section-title">Notification History</h2>
          <p className="admin-marketing-section-subtitle">View all sent and scheduled notifications</p>
        </div>
        <button className="admin-marketing-new-btn" onClick={onNewNotification}>
          <span>+</span>
          New Notification
        </button>
      </div>

      <div className="admin-marketing-filters">
        <div className="admin-marketing-search-box">
          <Search size={18} className="admin-marketing-search-icon" />
          <input
            type="text"
            className="admin-marketing-search-input"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
          />
        </div>
        <select
          className="admin-marketing-filter-select"
          value={filterType}
          onChange={(e) => handleFilterChange(setFilterType)(e.target.value)}
        >
          <option value="all">All Type</option>
          <option value="email">Email</option>
          <option value="in-app">In-App</option>
          <option value="both">Both</option>
        </select>
        <select
          className="admin-marketing-filter-select"
          value={filterStatus}
          onChange={(e) => handleFilterChange(setFilterStatus)(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="sent">Sent</option>
          <option value="scheduled">Scheduled</option>
          <option value="draft">Draft</option>
          <option value="sending">Sending</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="admin-marketing-table-container">
        {filteredNotifications.length === 0 ? (
          <div className="admin-marketing-empty-state">
            <BellOff size={56} className="admin-marketing-empty-icon" />
            <h3 className="admin-marketing-empty-title">No Notifications Found</h3>
            <p className="admin-marketing-empty-message">
              {!notificationHistory || notificationHistory.length === 0
                ? "You haven't sent any notifications yet. Create your first notification to get started!"
                : "No notifications match your current filters. Try adjusting your search or filter criteria."}
            </p>
          </div>
        ) : (
          <table className="admin-marketing-history-table">
            <thead>
              <tr>
                <th>NOTIFICATION</th>
                <th>TYPE</th>
                <th>AUDIENCE</th>
                <th>STATUS</th>
                <th>DATE</th>
                <th>PERFORMANCE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedNotifications.map(notif => (
                <tr key={notif.id}>
                  <td>
                    <div className="admin-marketing-notif-cell">
                      <div className="admin-marketing-notif-icon">
                        <Zap size={18} />
                      </div>
                      <div>
                        <div className="admin-marketing-notif-title">{notif.title}</div>
                        <div className="admin-marketing-notif-subtitle">{notif.subtitle}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="admin-marketing-type-badge">{getTypeLabel(notif.type)}</span>
                  </td>
                  <td>
                    <div className="admin-marketing-audience-cell">
                      {notif.audiences.map((aud, idx) => (
                        <div key={idx} className="admin-marketing-audience-tag">{aud}</div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span
                      className="admin-marketing-status-badge"
                      style={{ backgroundColor: getStatusColor(notif.status) + '20', color: getStatusColor(notif.status) }}
                    >
                      {notif.status.charAt(0).toUpperCase() + notif.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="admin-marketing-date-cell">
                      <div>{notif.date || '-'}</div>
                      <div className="admin-marketing-date-label">{notif.dateLabel}</div>
                    </div>
                  </td>
                  <td>
                    {notif.openRate ? (
                      <div className="admin-marketing-performance-cell">
                        <div className="admin-marketing-metric">
                          <Eye size={14} />
                          <span>{notif.openRate}%</span>
                        </div>
                        <div className="admin-marketing-metric">
                          <Zap size={14} />
                          <span>{notif.clickRate}%</span>
                        </div>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-marketing-actions">
                      <button
                        className="admin-marketing-action-btn"
                        onClick={() => onViewNotification(notif)}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button className="admin-marketing-action-btn" title="Copy">
                        <Copy size={16} />
                      </button>
                      <button className="admin-marketing-action-btn" title="Send">
                        <Send size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {filteredNotifications.length > 0 && totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '15px 20px',
            background: '#f9fafb',
            borderRadius: '8px'
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredNotifications.length)} of {filteredNotifications.length} notifications
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  background: currentPage === 1 ? '#f3f4f6' : 'white',
                  borderRadius: '6px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) e.currentTarget.style.background = 'white';
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <div style={{ display: 'flex', gap: '4px' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e5e7eb',
                      background: page === currentPage ? '#3b82f6' : 'white',
                      color: page === currentPage ? 'white' : '#374151',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      minWidth: '40px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (page !== currentPage) {
                        e.currentTarget.style.background = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== currentPage) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  background: currentPage === totalPages ? '#f3f4f6' : 'white',
                  borderRadius: '6px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) e.currentTarget.style.background = 'white';
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationHistory;
